import { FC, useMemo } from 'react';
import styled from 'styled-components';

import { VfoBarChart, VfoDoughnutChart, WidgetContainer } from '@/features/vfo/components';
import { getPriority, getStatusDistribution, getTasksKpi } from '@/features/vfo/helpers';
import { ApiDashboardResponseV2 } from '@/shared/api';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

type TasksDashboardProps = {
  analyticsData?: ApiDashboardResponseV2;
};

export const TasksDashboard: FC<TasksDashboardProps> = ({ analyticsData }) => {
  const { t } = useTranslation('features/vfo', { keyPrefix: 'dashboard' });

  const kpi = getTasksKpi(analyticsData);
  const statusItems = useMemo(() => {
    const statusDistribution = getStatusDistribution(analyticsData);

    return [
      {
        id: '1',
        color: COLORS.colorVfoTags[2],
        label: t('todo'),
        value: Math.round((statusDistribution.toDo / statusDistribution.totalTasks) * 100),
      },
      {
        id: '2',
        color: COLORS.colorVfoTags[0],
        label: t('inProgress'),
        value: Math.round((statusDistribution.inProgress / statusDistribution.totalTasks) * 100),
      },
      {
        id: '3',
        color: COLORS.systemMint200,
        label: t('done'),
        value: Math.round((statusDistribution.done / statusDistribution.totalTasks) * 100),
      },
    ];
  }, [analyticsData]);

  const priorityItems = useMemo(() => {
    const priorities = getPriority(analyticsData);

    return [
      {
        color: COLORS.colorVfoTags[2],
        label: t('high'),
        value: priorities.high,
      },
      {
        color: COLORS.colorVfoTags[6],
        label: t('medium'),
        value: priorities.medium,
      },
      {
        color: COLORS.colorVfoTags[0],
        label: t('low'),
        value: priorities.low,
      },
    ];
  }, [analyticsData]);

  return (
    <Container>
      <UsersStatisticsWrapper>
        <WidgetContainerStyled
          size="small"
          icon="note-pencil"
          title={`${kpi.newTasks} ${t('created')}`}
          subTitle={t('sevenDays')}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="clock-countdown"
          title={`${kpi.dueSoon} ${t('dueSoon')}`}
          subTitle={t('sevenDays')}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="arrows-counter-clockwise"
          title={`${kpi.updated} ${t('updated')}`}
          subTitle={t('sevenDays')}
          titleClassName="titleWrapper"
        />
        <WidgetContainerStyled
          size="small"
          icon="check-circle"
          title={`${kpi.completed} ${t('completed')}`}
          subTitle={t('sevenDays')}
          titleClassName="titleWrapper"
        />
      </UsersStatisticsWrapper>
      {kpi.totalTasks === 0 ? (
        <WidgetContainer style={{ flex: 1 }} title={t('tasksHealth')}>
          <NoJobsWrapper>
            <Icon style={{ color: COLORS.systemDark100 }} name="chart-donut" size="s44" />
            <b>{t('noTasksYet')}</b>
          </NoJobsWrapper>
        </WidgetContainer>
      ) : (
        <VfoDoughnutChartStyled
          title={t('statusOverview')}
          type="percent"
          legendSeparator
          items={statusItems}
        />
      )}
      <VfoBarChartStyled title={t('priorityBreakdown')} items={priorityItems} />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  gap: 24px;
  border-radius: 16px;
  align-items: stretch;

  ${media.md}, ${breakpoints.sm} {
    flex-wrap: wrap;
  }

  @container ${breakpoints.sm} {
    flex-direction: column;
    gap: 0;
    padding: 6px;
    background: ${COLORS.white};
  }
`;

const UsersStatisticsWrapper = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
  justify-content: space-between;
  width: 25%;

  ${media.md}, ${breakpoints.sm} {
    width: 190px;
  }

  @container ${breakpoints.sm} {
    flex-direction: row;
    gap: 8px;
    width: 100%;
    border-bottom: 1px solid ${COLORS.systemDark50A};
    padding-bottom: 12px;
    padding-top: 12px;
  }
`;

const VfoDoughnutChartStyled = styled(VfoDoughnutChart)`
  width: 50%;
  flex: unset;

  ${media.md}, ${breakpoints.sm} {
    flex: 1;
    width: auto;
  }
`;

const VfoBarChartStyled = styled(VfoBarChart)`
  flex: unset;
  width: 25%;

  ${media.md}, ${breakpoints.sm} {
    width: 100%;
  }

  @container ${breakpoints.sm} {
    padding: 16px;
  }
`;

const WidgetContainerStyled = styled(WidgetContainer)`
  &&& {
    @container ${breakpoints.sm} {
      padding: 0;
      flex: 1;

      .titleWrapper {
        flex-direction: column;
        gap: 6px;
        text-align: center;
        align-items: center;
        font-size: 12px;
      }
    }
  }
`;

export const NoJobsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  color: ${COLORS.systemDark200};
  height: 100%;
`;
